import { Flex, useScreenSize } from '@banhala/ui-kit';
import type { GoodsListRef } from '@banhala/ui-kit';
import { type ComponentRef, useCallback, useEffect, useRef } from 'react';
import type { NativeScrollEvent, NativeSyntheticEvent } from 'react-native';
import { useAnimatedReaction } from 'react-native-reanimated';
import type { SharedValue } from 'react-native-reanimated';
import { scheduleOnRN } from 'react-native-worklets';

import { useSafeTimeout } from '../../../../hooks';

// 가로 스크롤 nudge: 콘텐츠를 오른쪽으로 살짝 밀었다 복귀시켜 옆에 카드가 더 있음을 알린다.
const SCROLL_HINT_NUDGE_DISTANCE = 32;
const SCROLL_HINT_NUDGE_BACK_DELAY = 300;
// 2번째 행이 "노출됐다"고 보는 최소 비율 — 한 행의 이만큼이 viewport 안에 들어와야 한다.
const SECOND_ROW_REVEAL_RATIO = 1;
// screen 단위로 앱 세션 동안 1회만 노출한다.
const scrollHintShownScreens = new Set<string>();

/**
 * PDP 캐러셀 스크롤 인지 nudge.
 *
 * 부모 세로 스크롤이 멈춘 시점에, 2행 이상·로드모어 가능하며 캐러셀의 2번째 행이
 * viewport에 노출된 경우에 한해 screen당 1회 nudge를 재생한다. 가로로 더 볼 카드가
 * 있음을 알리는 것이 목적이라 2번째 행이 보이지 않는 시점엔 재생하지 않는다.
 *
 * - `carouselContainerRef`: 측정용. 캐러셀(헤더/배너 제외)만 감싼 컨테이너에 단다.
 * - `carouselRef`: nudge 재생용. UIKit GoodsCarousel에 단다.
 * - `handleScroll`: 캐러셀 가로 offset을 추적해 nudge 복귀 기준점을 잡는다.
 */
export const useGoodsCarouselScrollHint = ({
  screenKey,
  rowCount,
  canLoadMore,
  parentIsScrolling,
}: {
  screenKey: string;
  rowCount: number | undefined;
  canLoadMore: boolean;
  parentIsScrolling?: SharedValue<boolean>;
}) => {
  const setSafeTimeout = useSafeTimeout();
  const { height: screenHeight } = useScreenSize();

  const carouselContainerRef = useRef<ComponentRef<typeof Flex>>(null);
  const carouselRef = useRef<GoodsListRef>(null);
  const scrollOffsetXRef = useRef(0);

  const configRef = useRef({ rowCount: 1, canLoadMore: false });
  useEffect(() => {
    configRef.current = { rowCount: rowCount ?? 1, canLoadMore };
  }, [rowCount, canLoadMore]);

  const handleScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      scrollOffsetXRef.current = event.nativeEvent.contentOffset.x;
    },
    [],
  );

  // 현재 위치에서 오른쪽으로 살짝 밀었다 제자리로 복귀시킨다.
  const playNudge = useCallback(() => {
    const origin = scrollOffsetXRef.current;
    carouselRef.current?.scrollToOffset({
      offset: origin + SCROLL_HINT_NUDGE_DISTANCE,
      animated: true,
    });
    setSafeTimeout(() => {
      carouselRef.current?.scrollToOffset({ offset: origin, animated: true });
    }, SCROLL_HINT_NUDGE_BACK_DELAY);
  }, [setSafeTimeout]);

  const maybeShowHint = useCallback(() => {
    if (scrollHintShownScreens.has(screenKey)) return;
    const { rowCount: rows, canLoadMore: loadMore } = configRef.current;
    if (rows < 2 || !loadMore) return;
    carouselContainerRef.current?.measureInWindow(
      (_x: number, y: number, _width: number, height: number) => {
        if (height <= 0) return;
        // 멀티행 캐러셀 높이 = rowHeight * rowCount 이므로 height/rows 로 한 행
        // 높이를 추정. 2번째 행이 전부 화면 안에 들어오고(노출) 위로 잘려나가지
        // 않았을 때만 재생한다.
        const rowHeight = height / rows;
        const secondRowTop = y + rowHeight;
        const isSecondRowVisible =
          secondRowTop >= 0 &&
          secondRowTop + rowHeight * SECOND_ROW_REVEAL_RATIO <= screenHeight;
        if (!isSecondRowVisible) return;
        if (scrollHintShownScreens.has(screenKey)) return;
        scrollHintShownScreens.add(screenKey);
        playNudge();
      },
    );
  }, [screenKey, screenHeight, playNudge]);

  useAnimatedReaction(
    () => parentIsScrolling?.value ?? false,
    (isScrolling, prev) => {
      if (prev === true && isScrolling === false) {
        scheduleOnRN(maybeShowHint);
      }
    },
    [parentIsScrolling, maybeShowHint],
  );

  return { carouselContainerRef, carouselRef, handleScroll };
};
